/**
 * Oversikt (manager redesign, 2026-10-09; v3.1 #273): Dagen, the day as a list; Perioden, the month as
 * Planlegg → Kontroller → Rapporter; Oppfølging, what needs a decision across dates. Dagen and Oppfølging
 * read the same data, so the Oppfølging badge counts the same points the queue shows. Addresses: /hjem and
 * /dagen open Dagen (?d= a date, ?lag= a team, ?vis= an attendance filter, ?valgt= a shift's details),
 * /perioden opens Perioden (?mnd= YYYY-MM), /varsler opens Oppfølging.
 */
import { useShifts } from '@/api/data';
import { useCan } from '@/auth/caps';
import { T } from '@/i18n';
import { today as osloToday, tenantRules } from '@/rules';
import { useTenant } from '@/shell/tenant';
import { Button, Skeleton } from '@/ui';
import { PeriodControl } from '@/ui/PeriodControl';
import { DNL, monthLabel } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { PageHead, TabPanel, Tabs } from '@/ui/page';
import { useQueryClient } from '@tanstack/react-query';
import { addDays, dow } from '@timefly/core';
import { Link, useSearchParams } from 'react-router-dom';
import { shiftMonth } from '../Omsetning';
import { clock } from '../dagen/today';
import { useNow } from '../min/parts';
import { ProjectNotes } from '../prosjekter/Budget';
import { DagenTab } from './DagenTab';
import { OppfolgingTab } from './OppfolgingTab';
import { PeriodenTab } from './PeriodenTab';
import { dayRows, shiftsOfDay } from './day';
import { overviewLinks } from './navigation';
import { useFollowUpQueue } from './useQueue';
import './oversikt.css';

export type OversiktTab = 'dagen' | 'perioden' | 'oppfolging';

const MON = ['jan.', 'feb.', 'mars', 'apr.', 'mai', 'juni', 'juli', 'aug.', 'sep.', 'okt.', 'nov.', 'des.'];
/** «fredag 30. okt.» */
export const dayTitle = (d: string) =>
  `${(DNL[dow(d)] ?? '').toLowerCase()} ${Number(d.slice(8, 10))}. ${MON[Number(d.slice(5, 7)) - 1]}`;

export default function Oversikt({ tab, base }: { tab: OversiktTab; base?: string }) {
  const can = useCan();
  const { tenant, mode, workspace } = useTenant();
  const [sp, setSp] = useSearchParams();
  const qc = useQueryClient();
  const now = useNow(60_000).getTime();
  const today = osloToday();
  const d = /^\d{4}-\d{2}-\d{2}$/.test(sp.get('d') ?? '') ? (sp.get('d') as string) : today;
  const rota = mode === 'rota' && workspace.enabled.scheduling;
  const ym = /^\d{4}-(0[1-9]|1[0-2])$/.test(sp.get('mnd') ?? '')
    ? (sp.get('mnd') as string)
    : today.slice(0, 7);
  const seesTime = can('timeliste.se');

  // the day's list: from the day before, for a night shift or an open punch carried over
  const dayQ = useShifts(addDays(d, -1), d);

  const tol = tenantRules(tenant).toleranceMin ?? 15;
  const dayShifts = shiftsOfDay(dayQ.data?.shifts ?? [], d);
  const rows = dayRows(dayShifts, now, tol, rota);
  // the queue's attendance points are always today's: this list's rows when it shows today
  const fu = useFollowUpQueue(today, now);
  const queue = fu.queue;
  const queueUnknown = fu.loading || fu.failed;

  if (!tenant) return <Skeleton />;

  const links = overviewLinks(tab, sp, base === '/dagen' ? '/dagen' : '/hjem');
  const go = (day: string) => {
    const next = new URLSearchParams(sp);
    next.delete('valgt');
    if (day === today) next.delete('d');
    else next.set('d', day);
    setSp(next);
  };
  const goMonth = (m: string) => {
    const next = new URLSearchParams(sp);
    if (m === today.slice(0, 7)) next.delete('mnd');
    else next.set('mnd', m);
    setSp(next);
  };
  const updated = dayQ.dataUpdatedAt ? clock(new Date(dayQ.dataUpdatedAt).toISOString()) : null;
  return (
    <div className="ox">
      <PageHead
        icon="home"
        title={T('Oversikt')}
        sub={
          tab === 'dagen'
            ? T('Dagen din, samlet på ett sted.')
            : tab === 'perioden'
              ? T('Fra bemanning til lønnsgrunnlag.')
              : T('Det som venter på en avklaring.')
        }
        actions={
          tab === 'perioden' ? (
            <PeriodControl
              label={T('Måned')}
              month
              value={ym}
              onChange={goMonth}
              prevLabel={T('Forrige måned')}
              nextLabel={T('Neste måned')}
              onPrev={() => goMonth(shiftMonth(ym, -1))}
              onNext={() => goMonth(shiftMonth(ym, 1))}
            >
              <span aria-live="polite">{monthLabel(ym)}</span>
            </PeriodControl>
          ) : tab === 'dagen' ? (
            <PeriodControl
              value={d}
              onChange={go}
              label={T('Dag')}
              prevLabel={T('Forrige dag')}
              nextLabel={T('Neste dag')}
              onPrev={() => go(addDays(d, -1))}
              onNext={() => go(addDays(d, 1))}
              extra={
                d !== today ? (
                  <Button ghost onClick={() => go(today)}>
                    {T('I dag')}
                  </Button>
                ) : undefined
              }
            >
              <span aria-live="polite">{dayTitle(d)}</span>
            </PeriodControl>
          ) : (
            <Link className="btn" to={links.day}>
              <Icon name="calendar" />
              {T('Dagens oversikt')}
            </Link>
          )
        }
      >
        <div className="oxtabs">
          <Tabs
            label={T('Oversikt')}
            value={tab}
            items={[
              { key: 'dagen', label: T('Dagen'), icon: 'calendar' as const, to: links.day },
              ...(seesTime
                ? [
                    { key: 'perioden', label: T('Perioden'), icon: 'chart' as const, to: links.period },
                    {
                      key: 'oppfolging',
                      label: T('Oppfølging'),
                      icon: 'check' as const,
                      to: links.queue,
                      count: queueUnknown ? null : queue.points.length,
                      aria: queueUnknown
                        ? T('Oppfølging')
                        : T('Oppfølging, {n} punkter', { n: queue.points.length }),
                    },
                  ]
                : []),
            ]}
          />
          <div className="mfresh">
            {updated && <span>{T('Oppdatert {time}', { time: updated })}</span>}
            {/* every read on the page answers again; a read the role may not make is disabled and stays so */}
            <button
              type="button"
              className="mlink"
              onClick={() => void qc.invalidateQueries()}
              aria-label={T('Oppdater visning')}
            >
              <Icon name="refresh" />
            </button>
          </div>
        </div>
      </PageHead>
      {/* Hugin's word on the project budgets and the month close (#132), where projects are on */}
      {tab === 'dagen' && seesTime && <ProjectNotes />}
      <TabPanel tab={tab}>
        {tab === 'perioden' ? (
          <PeriodenTab ym={ym} today={today} now={now} onMonth={goMonth} queueLink={links.queue} />
        ) : tab === 'dagen' ? (
          <DagenTab
            d={d}
            today={today}
            now={now}
            rows={rows}
            rota={rota}
            shiftsQ={dayQ}
            dayShifts={dayShifts}
          />
        ) : (
          <OppfolgingTab
            queue={queue}
            loading={fu.loading}
            failed={fu.failed}
            unsure={fu.unsure}
            onRetry={fu.refetch}
          />
        )}
      </TabPanel>
    </div>
  );
}
